import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';

interface RoundState {
  id: string;
  name: string;
  status: 'disabled' | 'active' | 'completed';
  type: 'start' | 'finish' | 'client-declaration';
  operatorName?: string;
  completedAt?: Date;
}

interface TestRound {
  id: string;
  name: string;
  states: RoundState[];
  isActive: boolean;
}

interface RoundUserInfo {
  endUser?: any;
  startUser?: any;
  endTime?: string;
  startTime?: string;
}

interface StatusRoundMapping {
  [key: string]: {
    roundKey: string;
    stateType: 'start' | 'finish' | 'client-declaration';
    userInfoKey: string;
    endUserKey: string;
    startUserKey: string;
    endTimeKey: string;
    startTimeKey: string;
  };
}

@Component({
  selector: 'projects-round-testlist',
  imports: [CommonModule],
  templateUrl: './projects-round-testlist.component.html',
  styleUrl: './projects-round-testlist.component.scss'
})
export class ProjectsRoundTestlistComponent implements OnInit, OnChanges {
  @Input() projectId?: string;
  @Input() batchId?: number;
  @Input() readonly: boolean = false;
  @Input() testResults?: any;
  @Input() initialRoundData?: any;
  @Input() restrictToClientDeclaration: boolean = false;
  
  @Output() onRoundStateChanged = new EventEmitter<{roundId: string, stateId: string, status: string}>();
  @Output() onRoundCompleted = new EventEmitter<{roundId: string, completedAt: Date}>();
  @Output() onRoundDataUpdated = new EventEmitter<any>();

  testRounds: TestRound[] = [];
  currentOperatorName: string = '';
  private apiRoundData: any = null;

  private statusRoundMapping: StatusRoundMapping = {
    'ROUND1_STARTED': {
      roundKey: 'round1',
      stateType: 'start',
      userInfoKey: 'round1StartUser',
      endUserKey: 'round1EndUser',
      startUserKey: 'round1StartUser',
      endTimeKey: 'round1EndTime',
      startTimeKey: 'round1StartTime'
    },
    'ROUND1_ENDED': {
      roundKey: 'round1',
      stateType: 'finish',
      userInfoKey: 'round1EndUser',
      endUserKey: 'round1EndUser',
      startUserKey: 'round1StartUser',
      endTimeKey: 'round1EndTime',
      startTimeKey: 'round1StartTime'
    },
    'CLIENT_CONFIRMED1': {
      roundKey: 'client-declaration',
      stateType: 'client-declaration',
      userInfoKey: 'nextRoundUser',
      endUserKey: 'nextRoundUser',
      startUserKey: 'nextRoundUser',
      endTimeKey: 'nextRoundTime',
      startTimeKey: 'nextRoundTime'
    },
    'ROUND2_STARTED': {
      roundKey: 'round2',
      stateType: 'start',
      userInfoKey: 'round2StartUser',
      endUserKey: 'round2EndUser',
      startUserKey: 'round2StartUser',
      endTimeKey: 'round2EndTime',
      startTimeKey: 'round2StartTime'
    },
    'ROUND2_ENDED': {
      roundKey: 'round2',
      stateType: 'finish',
      userInfoKey: 'round2EndUser',
      endUserKey: 'round2EndUser',
      startUserKey: 'round2StartUser',
      endTimeKey: 'round2EndTime',
      startTimeKey: 'round2StartTime'
    }
  };

  constructor(private projectsService: ProjectsService, private alertNotificationService: AlertNotificationService) {}

  ngOnInit(): void {
    if (this.initialRoundData) {
      this.testRounds = this.initialRoundData;
    } else {
      this.initializeTestRounds();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['batchId'] && !changes['batchId'].firstChange) {
      if (this.initialRoundData) {
        this.testRounds = this.initialRoundData;
      } else {
        this.initializeTestRounds();
      }
    }
    
    if (changes['initialRoundData'] && !changes['initialRoundData'].firstChange) {
      if (this.initialRoundData) {
        this.testRounds = this.initialRoundData;
      } else {
        this.initializeTestRounds();
      }
    }
  }

  private initializeTestRounds(): void {
    this.testRounds = [
      {
        id: 'round1',
        name: '1° Round',
        isActive: true,
        states: [
          {
            id: 'round1-start',
            name: 'Started',
            type: 'start',
            status: 'active'
          },
          {
            id: 'round1-finish',
            name: 'Finished',
            type: 'finish',
            status: 'disabled'
          }
        ]
      },
      {
        id: 'client-declaration',
        name: 'Client declaration - All issues are fixed',
        isActive: false,
        states: [
          {
            id: 'client-go-ahead',
            name: 'Go ahead from the client',
            type: 'client-declaration',
            status: 'disabled'
          }
        ]
      },
      {
        id: 'round2',
        name: '2° Round',
        isActive: false,
        states: [
          {
            id: 'round2-start',
            name: 'Started',
            type: 'start',
            status: 'disabled'
          },
          {
            id: 'round2-finish',
            name: 'Finished',
            type: 'finish',
            status: 'disabled'
          }
        ]
      }
    ];
  }

  onStateClick(roundId: string, stateId: string): void {
    if (this.readonly) return;

    const round = this.testRounds.find(r => r.id === roundId);
    if (!round || !round.isActive) return;

    const state = round.states.find(s => s.id === stateId);
    if (!state || state.status !== 'active') return;

    const nextStatus = this.getNextStatus(roundId, stateId);
    if (!nextStatus) {
      this.alertNotificationService.error('Unable to determine next status');
      return;
    }

    if (this.projectId && this.batchId) {
      this.projectsService.updateProjectTestlistStatusRounds(
        Number(this.projectId),
        this.batchId,
        nextStatus,
        {} 
      ).subscribe({
        next: (response) => {
          this.apiRoundData = response.data?.projectRoundGroupedInfo;
          
          const extractedData = this.extractOperatorDataFromResponse(nextStatus);
          
          state.status = 'completed';
          state.operatorName = extractedData.operatorName;
          state.completedAt = extractedData.completedAt;

          this.onRoundStateChanged.emit({
            roundId,
            stateId,
            status: 'completed'
          });
          
          const isRoundFinished = this.isRoundFinished(round);
          if (isRoundFinished) {
            this.onRoundCompleted.emit({
              roundId,
              completedAt: state.completedAt
            });
          }

          this.activateNextState(roundId, stateId);
          this.emitRoundDataUpdate();

          this.alertNotificationService.success(`Round status updated to ${nextStatus}`);
        },
        error: (error) => {
          this.alertNotificationService.error(`Error updating round status: ${error.message || 'Unknown error'}`);
          console.error('Error updating round status:', error);
        }
      });
    } else {
      this.alertNotificationService.error('Missing required parameters: projectId or batchId');
    }
  }

  private getNextStatus(roundId: string, stateId: string): string | null {
    if (roundId === 'round1' && stateId === 'round1-start') {
      return 'ROUND1_STARTED';
    } else if (roundId === 'round1' && stateId === 'round1-finish') {
      return 'ROUND1_ENDED';
    } else if (roundId === 'client-declaration' && stateId === 'client-go-ahead') {
      return 'CLIENT_CONFIRMED1';
    } else if (roundId === 'round2' && stateId === 'round2-start') {
      return 'ROUND2_STARTED';
    } else if (roundId === 'round2' && stateId === 'round2-finish') {
      return 'ROUND2_ENDED';
    }
    
    return null;
  }

  private extractOperatorDataFromResponse(status: string): { operatorName: string; completedAt: Date } {
    const mapping = this.statusRoundMapping[status];
    
    if (!mapping || !this.apiRoundData) {
      return {
        operatorName: this.currentOperatorName,
        completedAt: new Date()
      };
    }

    const userInfoKey = this.getRelevantUserInfoKey(status, mapping);
    const user = this.apiRoundData[userInfoKey];
    const timeKey = this.getRelevantTimeKey(status, mapping);
    const time = this.apiRoundData[timeKey];

    const operatorName = this.formatUserName(user);
    const completedAt = time ? new Date(time) : new Date();

    return {
      operatorName,
      completedAt
    };
  }

  private getRelevantUserInfoKey(status: string, mapping: any): string {
    if (mapping.stateType === 'finish' || mapping.stateType === 'client-declaration') {
      return mapping.endUserKey;
    }
    return mapping.startUserKey;
  }

  private getRelevantTimeKey(status: string, mapping: any): string {
    if (mapping.stateType === 'finish' || mapping.stateType === 'client-declaration') {
      return mapping.endTimeKey;
    }
    return mapping.startTimeKey;
  }

  private formatUserName(user: any): string {
    if (!user) {
      return this.currentOperatorName;
    }

    if (user.firstName && user.lastName) {
      return `${user.lastName} ${user.firstName}`;
    }

    if (user.lastName) {
      return user.lastName;
    }

    if (user.firstName) {
      return user.firstName;
    }

    return this.currentOperatorName;
  }

  private activateNextState(roundId: string, completedStateId: string): void {
    const round = this.testRounds.find(r => r.id === roundId);
    if (!round) return;

    const completedIndex = round.states.findIndex(s => s.id === completedStateId);
    
    if (completedIndex < round.states.length - 1) {
      round.states[completedIndex + 1].status = 'active';
    } else {
      round.isActive = false;
      const nextRound = this.getNextRound(roundId);
      if (nextRound) {
        nextRound.isActive = true;
        if (nextRound.states.length > 0) {
          nextRound.states[0].status = 'active';
        }
      }
    }
  }

  private getNextRound(currentRoundId: string): TestRound | undefined {
    const currentIndex = this.testRounds.findIndex(r => r.id === currentRoundId);
    return currentIndex < this.testRounds.length - 1 ? 
      this.testRounds[currentIndex + 1] : undefined;
  }

  getButtonClass(state: RoundState): string {
    switch (state.status) {
      case 'active':
        return state.type === 'start' || state.type === 'client-declaration' ? 
          'btn btn-success' : 'btn btn-danger';
      case 'completed':
        return state.type === 'start' || state.type === 'client-declaration' ? 
          'btn btn-success' : 'btn btn-danger';
      case 'disabled':
      default:
        return state.type === 'start' || state.type === 'client-declaration' ? 
          'btn btn-outline-success' : 'btn btn-outline-danger';
    }
  }

  isButtonDisabled(state: RoundState): boolean {
    if (this.readonly || state.status === 'disabled' || state.status === 'completed') return true;
    if (this.restrictToClientDeclaration && state.type !== 'client-declaration') return true;
    return false;
  }

  hasIssues(roundId: string): boolean {
    return roundId === 'round1';
  }

  getIssueCount(roundId: string): number {
    return roundId === 'round1' ? 1 : 0;
  }

  isRoundCompleted(roundIndex: number): boolean {
    const round = this.testRounds[roundIndex];
    if (!round || round.isActive) return false;
    
    const activeRoundIndex = this.testRounds.findIndex(r => r.isActive);
    return activeRoundIndex > roundIndex;
  }

  private isRoundFinished(round: TestRound): boolean {
    return round.states.every(state => state.status === 'completed');
  }

  resetRounds(): void {
    this.initializeTestRounds();
  }

  getCurrentActiveRound(): TestRound | undefined {
    return this.testRounds.find(r => r.isActive);
  }

  getCompletedRounds(): TestRound[] {
    return this.testRounds.filter(r => !r.isActive && this.isRoundFinished(r));
  }

  setOperatorName(name: string): void {
    this.currentOperatorName = name;
  }

  getProgressPercentage(): number {
    const totalStates = this.testRounds.reduce((acc, round) => acc + round.states.length, 0);
    const completedStates = this.testRounds.reduce((acc, round) => {
      return acc + round.states.filter(s => s.status === 'completed').length;
    }, 0);
    
    return totalStates > 0 ? Math.round((completedStates / totalStates) * 100) : 0;
  }

  private emitRoundDataUpdate(): void {
    this.onRoundDataUpdated.emit(JSON.parse(JSON.stringify(this.testRounds)));
  }
}
